import {
  ArrowRight,
  Bath,
  BookOpen,
  Building2,
  CalendarDays,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Dog,
  FileText,
  Flower2,
  Heart,
  House,
  MapPin,
  MessageSquare,
  Scissors,
  Search,
  Settings,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Stethoscope,
  Store,
  UserRound,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { BusinessType } from '@app/shared';

/**
 * Зуб — единственная своя иконка. Подписана как `LucideIcon`, потому что принимает тот же
 * набор свойств (`size`, `strokeWidth`, `color` через `currentColor`) и обязана вести себя
 * как остальные: иначе одна плитка в сетке отраслей жила бы по своим правилам.
 *
 * Сетка 24×24 и `stroke-linejoin: round` — как у Lucide, иначе стык линий у этой иконки
 * был бы острым, а у соседних скруглённым, и разнобой читался бы как небрежность.
 */
const Tooth = (({ size = 24, strokeWidth = 2, ...rest }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    <path d="M7.5 3C5 3 3 5 3 7.7c0 1.8.5 2.9.9 4.6.4 1.9.6 4.4 1.1 5.9.3 1 .8 1.8 1.5 1.8.9 0 1.2-1 1.4-2.1l.7-3.7c.2-1 .6-1.4 1.4-1.4s1.2.4 1.4 1.4l.7 3.7c.2 1.1.5 2.1 1.4 2.1.7 0 1.2-.8 1.5-1.8.5-1.5.7-4 1.1-5.9.4-1.7.9-2.8.9-4.6C17 5 15 3 12.5 3c-1 0-1.7.3-2.5.6-.8-.3-1.5-.6-2.5-.6Z" />
  </svg>
)) as LucideIcon;

/**
 * Иконки интерфейса — контурные, из Lucide (макет, легенда «Иконки»: 24×24, толщина 1.75–2).
 *
 * До 16 сентября 2026 отрасли и вкладки рисовались эмодзи. Эмодзи рисует системный шрифт:
 * один и тот же 💈 выглядит по-разному на iOS и Android, приезжает цветным посреди
 * монохромного интерфейса и не подчиняется палитре — то есть единственный элемент экрана,
 * который нельзя покрасить. Отсюда набор контурных иконок: они наследуют `currentColor`
 * и живут по тем же правилам, что текст.
 *
 * Карта полная по `BusinessType`: `Record` без `Partial` — компилятор потребует иконку
 * для новой отрасли, и она не уедет в каталог безымянным пустым квадратом.
 *
 * Зуб нарисован здесь же, `Tooth` ниже: в Lucide его нет вовсе (легенда макета допускает
 * «Lucide или Iconoir», и зуб дизайнер взял не из Lucide). Свой путь в собственном файле
 * дешевле второй библиотеки иконок ради одной картинки.
 */
export const INDUSTRY_ICON: Readonly<Record<BusinessType, LucideIcon>> = {
  barbershop: Scissors,
  beauty: Sparkles,
  dental: Tooth,
  massage: Flower2,
  autoservice: Wrench,
  tutor: BookOpen,
  vet: Dog,
  grooming: Bath,
  doctor: Stethoscope,
};

/** Иконки навигации и служебных мест. Собраны здесь же, чтобы толщина линии задавалась одним местом. */
export {
  ArrowRight,
  Building2,
  CalendarDays,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  Heart,
  House,
  MapPin,
  MessageSquare,
  Search,
  Settings,
  ShieldCheck,
  Smartphone,
  Star,
  Store,
  UserRound,
  type LucideIcon,
};

/**
 * Толщина линии с макета. Числом в одном месте, а не строкой в каждом вызове: Lucide
 * по умолчанию рисует 2, а легенда допускает 1.75–2, и разнобой между экранами заметен
 * ровно настолько, насколько незаметна причина.
 */
export const ICON_STROKE = 1.75;
